import { inject, Injectable } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	CanActivate,
	GuardResult,
	Router,
	RouterStateSnapshot,
} from '@angular/router';
import { authState, FIREBASE_AUTH } from '@context/frontend/common';
import { User } from 'firebase/auth';
import { map, Observable, OperatorFunction, pipe, take } from 'rxjs';

/** An rxjs operator mapping the Firebase auth user to a route result. */
export type AuthPipe = OperatorFunction<
	User | null,
	boolean | string | unknown[]
>;

/** Factory that produces an {@link AuthPipe} for a given route. */
export type AuthPipeGenerator = (
	next: ActivatedRouteSnapshot,
	state: RouterStateSnapshot,
) => AuthPipe;

/** Emits `true` when a user is signed in, `false` otherwise. */
export const loggedIn: AuthPipe = map((user) => !!user);

/**
 * Route guard that mirrors the behavior of the removed `@angular/fire/auth-guard`
 * `AuthGuard`. It reads an `authGuardPipe` factory from the route's `data` and pipes
 * the current Firebase auth user through it, redirecting based on the result.
 */
@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
	private readonly router = inject(Router);
	private readonly auth = inject(FIREBASE_AUTH);

	canActivate(
		next: ActivatedRouteSnapshot,
		state: RouterStateSnapshot,
	): Observable<GuardResult> {
		const authPipeFactory =
			(next.data['authGuardPipe'] as AuthPipeGenerator) ||
			(() => loggedIn);

		return authState(this.auth).pipe(
			take(1),
			authPipeFactory(next, state),
			map((can) => {
				if (typeof can === 'boolean') return can;
				if (Array.isArray(can)) return this.router.createUrlTree(can);
				return this.router.parseUrl(can);
			}),
		);
	}
}

/**
 * Convenience helper matching `@angular/fire`'s `canActivate`: wires the guard and
 * the provided pipe factory onto a route definition.
 */
export const canActivate = (pipe: AuthPipeGenerator) => ({
	canActivate: [AuthGuard],
	data: { authGuardPipe: pipe },
});

/** Redirects to `redirect` when the user is not authenticated. */
export const redirectUnauthorizedTo = (
	redirect: string | unknown[],
): AuthPipe =>
	pipe(
		loggedIn,
		map((isLoggedIn) => isLoggedIn || redirect),
	);

/** Redirects to `redirect` when the user is authenticated. */
export const redirectLoggedInTo = (redirect: string | unknown[]): AuthPipe =>
	pipe(
		loggedIn,
		map((isLoggedIn) => (isLoggedIn && redirect) || true),
	);
